import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { BsCalendar3, BsBoxArrowUpRight } from 'react-icons/bs';
import { getThisWeekInHistory } from '../../api/knowledge';
import { trackEvent } from '../../utils/analytics';

const MONTH_NAMES = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];

export default function ThisWeekInHistory() {
  const [event, setEvent] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    getThisWeekInHistory()
      .then((data) => {
        const events = data?.events;
        if (events?.length > 0) {
          setEvent(events[0]);
          trackEvent('knowledge_this_week_viewed', { count: events.length });
        }
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <div className="glass rounded-2xl p-5 animate-pulse h-full">
        <div className="h-3 w-20 bg-white/10 rounded mb-3" />
        <div className="h-3 w-16 bg-white/10 rounded mb-2" />
        <div className="h-4 w-3/4 bg-white/10 rounded mb-2" />
        <div className="h-3 w-full bg-white/5 rounded mb-1" />
        <div className="h-3 w-4/5 bg-white/5 rounded" />
      </div>
    );
  }

  if (!event) return null;

  const meta = event.metadata || {};
  const dateLabel = meta.month && meta.day
    ? `${MONTH_NAMES[meta.month - 1] || ''} ${meta.day}`
    : null;

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3, delay: 0.1 }}
      className="glass rounded-2xl p-5 h-full flex flex-col"
    >
      {/* Header */}
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <BsCalendar3 className="text-brand-400 text-base" />
          <span className="text-[11px] font-semibold text-gray-400 uppercase tracking-wider">This week in history</span>
        </div>
        {event.category && (
          <span className="text-[10px] font-medium text-brand-400 bg-brand-500/10 px-2 py-0.5 rounded-full">
            {event.category}
          </span>
        )}
      </div>

      {/* Date */}
      {dateLabel && (
        <div className="flex items-center gap-2 mb-2">
          <span className="text-xs font-bold text-brand-400 font-display">{dateLabel}</span>
          {meta.year && <span className="text-xs text-gray-500">{meta.year}</span>}
        </div>
      )}

      {/* Title */}
      <h3 className="text-base font-bold text-white mb-2 font-display leading-snug">
        {event.title}
      </h3>

      {/* Content */}
      <p className="text-gray-400 text-[13px] leading-relaxed flex-1 line-clamp-3">
        {event.content || event.summary}
      </p>

      {/* Source */}
      {event.source?.name && (
        <div className="mt-3 pt-3 border-t border-white/5">
          <a
            href={event.source.url || '#'}
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => trackEvent('knowledge_source_clicked', {
              external_id: event.external_id,
              source: event.source.name,
            })}
            className="text-[11px] text-gray-500 hover:text-gray-300 transition-colors inline-flex items-center gap-1"
          >
            {event.source.name}
            <BsBoxArrowUpRight className="text-[9px]" />
          </a>
        </div>
      )}
    </motion.div>
  );
}
